<template>
    <section class="fto-modal">
        <img :src="iconNotEnoughBalance"
            class="fto-modal__image"
        >

        <div class="fto-modal__title">{{ $t('fanTokenNeededError.sellTitle') }}</div>

        <div class="fto-modal__subtitle">{{ subTitle }}</div>

        <div class="fto-modal__table">
            <div class="fto-modal__row">
                {{ $t('buyError.yourBalance') }}

                <div class="fto-modal__amount fto-modal__amount--red">
                    {{ isSellPage ? balanceTokens : balanceChz }}

                    <span v-if="isChzAllowed">{{ isSellPage ? balanceChz : balanceFiat }}</span>
                </div>
            </div>

            <div class="fto-modal__row">
                {{ $t('buyError.amountNeeded') }}

                <div class="fto-modal__amount">
                    {{ amountNeeded }}

                    <span v-if="isChzAllowed">{{ amountNeededBase }}</span>
                </div>
            </div>
        </div>

        <div class="fto-modal__disclaimer">{{ $t('buyChz.disclaimer') }}</div>
        <div class="buttons">
            <div id="button-confirm"
                :class="['buttons__item buttons__item--success',{'buttons__item--disabled':!isTopupEnabled}]"
                @click="buyChiliz"
            >
                {{ $t('buyError.topUpNow') }}
            </div>
            <div v-if="!isTopupEnabled"
                class="topup-message--disabled"
            >
                {{ $t('topup.unavailable') }}
            </div>

            <div id="button-cancel"
                class="buttons__item"
                @click="goToWallet"
            >
                {{ $t('sendOut.processingBack') }}
            </div>
        </div>
    </section>
</template>

<script>
// 3rd Party
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Local store
import localStoreTypes from '@webapp/store/types.js';

// Utils
import {
    formatPrice,
} from '@shared/utils/formatter.js';

export default {
    name: 'not-enough-amount-modal',
    data() {
        return {
            balance: 0,
            fiatBalance: 0,
            price: 0,
            rate: 0,
            fiatPrice: 0,
            partner: {},
            isSellPage: false,
        };
    },
    computed: {
        ...mapGetters([
            'preferredFiatCurrency',
            'modal',
        ]),

        ...mapGetters('wallet', [
            'isChzAllowed',
            'centralizedWallet',
            'isTopupEnabled',
        ]),

        balanceTokens() {
            return formatPrice(this.balance, this.partner.tokenSymbol, true);
        },

        balanceChz() {
            return formatPrice(
                this.isSellPage ? this.balance * this.rate : this.balance,
                this.isChzAllowed ? '$CHZ' : this.preferredFiatCurrency.shortCode, true
            );
        },

        balanceFiat() {
            return formatPrice(this.fiatBalance, this.preferredFiatCurrency.shortCode);
        },

        amountNeeded() {
            if (this.isSellPage) {
                return formatPrice(this.price, this.partner.tokenSymbol, true);
            }

            return formatPrice(this.price, (this.isChzAllowed ? '$CHZ' : this.preferredFiatCurrency.shortCode), true);
        },

        amountNeededBase() {
            return formatPrice(this.fiatPrice, this.isSellPage ? '$CHZ' : this.preferredFiatCurrency.shortCode);
        },

        subTitle() {
            if (this.isSellPage) {
                return this.$t('fanTokenNeededError.sellMessage', {
                    name: this.partner.shortName,
                    currencyShortCode: this.centralizedWallet.currency.shortCodeFormatted,
                });
            }

            return (this.isChzAllowed ? this.$t('buyError.balanceNotSufficient') : this.$t('buyError.fiatBalanceNotSufficient'));
        },

        iconNotEnoughBalance() {
            return this.isChzAllowed ? require('@webapp/assets/not-enough-chz.webp') : require('@webapp/assets/not-enough-fiat.webp');
        },
    },
    watch: {
        'modal.data': {
            immediate: true,
            handler(value) {
                if (Object.keys(value)) {
                    this.balance = value.balance || 0;
                    this.fiatBalance = value.fiatBalance || 0;
                    this.price = value.price || 0;
                    this.rate = value.rate || 0;
                    this.fiatPrice = value.fiatPrice || 0;
                    this.partner = value.partner || {};
                    this.isSellPage = value.isSellPage || false;
                }
            },
        },
    },
    methods: {
        ...mapActions({
            resetModal: localStoreTypes.RESET_MODAL,
        }),
        buyChiliz() {
            this.resetModal();
            this.$router.push({
                name: 'topup',
                params: {
                    amountToCompleteFTPurchase: this.price,
                },
            });
        },
        goToWallet() {
            this.resetModal();
            this.$router.push('/wallet');
        },
    },
};
</script>

<style lang="scss" scoped>
.fto-modal {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: $cyprus-color;

    &__image {
        margin-top: 8rem;
        object-fit: cover;
        max-width: 30%;

        @include max-width(sm) {
            max-width: 20rem;
        }
    }

    &__title {
        padding: 4.8rem 0 2rem;
        font-size: 2.5rem;
        font-weight: 700;
        line-height: 3.4rem;

        @include max-width(sm) {
            font-size: 2rem;
            line-height: 2.2rem;
            padding: 3.5rem 0 2rem;
        }
    }

    &__subtitle {
        font-size: 1.5rem;
        line-height: 2rem;
        white-space: pre-line;

        @include max-width(sm) {
            font-size: 1.4rem;
        }
    }

    &__table {
        width: 33.8rem;
        margin: 3.4rem 0;
        border-top: 0.1rem solid $alto-color;
        border-bottom: 0.1rem solid $alto-color;

        @include max-width(sm) {
            width: 30rem;
            margin: 2rem 0;
        }
    }

    &__row {
        display: flex;
        justify-content: space-between;
        padding: 2rem 2.4rem 2rem 1.1rem;
        font-size: 1.5rem;
        line-height: 2rem;
        font-weight: 600;

        &:first-of-type {
            border-bottom: 0.1rem solid $alto-color;
        }

        @include max-width(sm) {
            font-size: 1.4rem;
        }
    }

    &__amount {
        text-align: right;

        &--red {
            color: $monza-color;
        }

        span {
            font-size: 1.2rem;
            line-height: 1.5rem;
            font-weight: 400;
            display: block;
        }
    }

    &__disclaimer {
        margin-bottom: 1rem;
    }
}

.buttons {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin: 0 0 2rem;
    width: 100%;

    &__item {
        text-align: center;
        font-size: 1.5rem;
        line-height: 1.5rem;
        width: 34rem;
        margin: 2rem 0 0;
        cursor: pointer;

        @include max-width(sm) {
            width: 25rem;
        }

        &--success {
            margin-top: 0;
            line-height: 4rem;
            border-radius: 0.4rem;
            color: $daintree-color;
            background: $lima-color;
        }

        &--disabled {
            opacity: 0.5;
            pointer-events: none;
            cursor: not-allowed;
        }
    }
}

.topup-message {
    &--disabled {
        margin-top: 8px;
    }
}
</style>